
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useSocket } from './SocketContext';

const ITAdminContext = createContext({});

function parseJSON(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { return JSON.parse(v); } catch(e) { return []; } }
  return [];
}
function mapCred(c) {
  return { empId: c.emp_id||c.empId||'', empName: c.emp_name||c.empName||'', lapUser: c.laptop_user||c.lapUser||'', lapPass: c.laptop_pass||c.lapPass||'', ip: c.ip_address||c.ip||'', software: parseJSON(c.software), ai: parseJSON(c.ai_tools||c.ai), vpn: c.vpn_enabled!=null?!!c.vpn_enabled:!!c.vpn, email: c.email||'', sysAccess: parseJSON(c.system_access||c.sysAccess), userId: c.user_id };
}
function mapReq(r) {
  return { rid: r.req_id||r.rid||'', empName: r.emp_name||r.empName||'', empId: r.emp_id||r.empId||'', type: r.type||'', detail: r.detail||'', date: r.date||'', status: r.status||'pending', remarks: r.remarks||'', userId: r.user_id };
}
function mapAsset(a) {
  return {
    // Keep both naming conventions so dashboard + assets page both work
    assetId: a.asset_id||a.assetId||'',
    asset_id: a.asset_id||a.assetId||'',
    empId: a.assigned_emp_id||a.emp_id||a.assigned_to||a.empId||'',
    emp_name: a.emp_name||a.empName||'',
    empName: a.emp_name||a.empName||'',
    type: a.asset_type||a.type||'',
    asset_type: a.asset_type||a.type||'',
    brand: a.brand||'',
    serial: a.serial||'',
    issued: a.issued_date||a.issued||'',
    issued_date: a.issued_date||a.issued||'',
    condition: a.condition||'Good',
    status: a.status||'Available',
    warranty: a.warranty||'',
    // Maintenance dates — critical for dashboard count
    next_maintenance_date: a.next_maintenance_date||'',
    last_maintenance_date: a.last_maintenance_date||'',
    // Other fields
    asset_tag: a.asset_tag||'',
    asset_value: a.asset_value||0,
    warranty_date: a.warranty_date||'',
    purchased_date: a.purchased_date||'',
  };
}

export function ITAdminProvider({ children }) {
  const { connected, on, off } = useSocket();
  const [d, setD] = useState({
    loaded: false,
    itaPeople: [], itaAssets: [],
    itaCreds: [], itaReqs: [],
    itaActionLogs: [], itaExitData: {},
  });

  const fetchAll = useCallback(async () => {
    const [people, assets, creds, reqs, logs] = await Promise.all([
      api.getITPeople().catch(()=>[]),
      api.getITAssets().catch(()=>[]),
      api.getITCredentials().catch(()=>[]),
      api.getITRequests().catch(()=>[]),
      api.getITLogs().catch(()=>[]),
    ]);
    const exitMap = {};
    setD(prev => ({
      ...prev, loaded: true,
      itaPeople: people || [],
      itaAssets: (assets||[]).map(mapAsset),
      itaCreds: (creds||[]).map(mapCred),
      itaReqs: (reqs||[]).map(mapReq),
      itaActionLogs: (logs||[]).map(l => ({
        ts: l.timestamp_str||l.ts||'', admin: l.admin_name||l.admin||'',
        action: l.action||'', target: l.target||'', detail: l.detail||''
      })),
      itaExitData: exitMap,
    }));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!connected) return;
    const handle = () => { fetchAll().catch(() => {}); };
    on('notification', handle);
    on('notification:new', handle);
    on('asset:update', handle);
    on('it:request', handle);
    on('exit:update', handle);
    return () => {
      off('notification', handle);
      off('notification:new', handle);
      off('asset:update', handle);
      off('it:request', handle);
      off('exit:update', handle);
    };
  }, [connected, on, off, fetchAll]);

  return <ITAdminContext.Provider value={{ ...d, refreshITAdminData: fetchAll }}>{children}</ITAdminContext.Provider>;
}

export const useITAdminData = () => useContext(ITAdminContext);
